import { CircularGallery, type GalleryItem } from "./CircularGallery";
import { useLocale } from "../i18n/useLocale";
import { cmsImage } from "../i18n/cmsImage";

/**
 * IndustriesGallery — the rotating 3D card band of industries Done-it serves.
 * Cards only, no heading (used on the home page right after FeaturesDetail).
 * Data is shared with the /gallery-lab preview and the footer marquee.
 *
 * Each card's photo is a CMS slot (`industries:bento.photo.<key>`) with the
 * unsplash shot as the baked fallback — so every industry image is editable
 * from the admin, one field per industry.
 */

type TFn = ReturnType<typeof useLocale>["t"];

const IMG = "https://images.unsplash.com";
const opt = "?w=900&auto=format&fit=crop&q=80";

// key = the CMS slug suffix (matches the admin's `industries.bento.photo.*`).
const GALLERY: (GalleryItem & { key: string })[] = [
  { key: "construction", title: "Construction", subtitle: "Location-verified site check-in", image: { url: `${IMG}/photo-1503387762-592deb58ef4e${opt}`, alt: "construction site with cranes", pos: "50% 40%" } },
  { key: "cleaning", title: "Cleaning", subtitle: "CIAO-ready presence per site", image: { url: `${IMG}/photo-1581578731548-c64695cc6952${opt}`, alt: "professional cleaning at work", pos: "50% 45%" } },
  { key: "warehousing", title: "Warehousing", subtitle: "Shift hours across the floor", image: { url: `${IMG}/photo-1586528116311-ad8dd3c8310d${opt}`, alt: "warehouse with stocked shelves", pos: "50% 50%" } },
  { key: "fieldService", title: "Field service", subtitle: "Mileage and time, per visit", image: { url: `${IMG}/photo-1581092160562-40aa08e78837${opt}`, alt: "field technician on site", pos: "50% 40%" } },
  { key: "salons", title: "Salons", subtitle: "Shift hours, per stylist", image: { url: `${IMG}/photo-1599351431202-1e0f0137899a${opt}`, alt: "barber cutting a client's hair", pos: "50% 30%" } },
  { key: "electricians", title: "Electricians", subtitle: "Project hours, audit-ready", image: { url: `${IMG}/photo-1621905251189-08b45d6a269e${opt}`, alt: "electrician wiring a panel", pos: "50% 45%" } },
  { key: "plumbing", title: "Plumbing", subtitle: "Time and materials per job", image: { url: `${IMG}/photo-1607472586893-edb57bdc0e39${opt}`, alt: "plumber working on pipes", pos: "50% 50%" } },
  { key: "landscaping", title: "Landscaping", subtitle: "Crew hours across every site", image: { url: `${IMG}/photo-1416879595882-3373a0480b5b${opt}`, alt: "landscaped green space", pos: "50% 55%" } },
  { key: "maintenance", title: "Maintenance", subtitle: "Tasks and reports with photos", image: { url: `${IMG}/photo-1530124566582-a618bc2615dc${opt}`, alt: "maintenance technician at work", pos: "50% 40%" } },
  // Non-technical trades — the client's point: most of his buyers are shops,
  // offices and salons, and the strip showed only crews in hi-vis.
  { key: "retail", title: "Shops & retail", subtitle: "Shift clock-in across branches", image: { url: `${IMG}/photo-1441986300917-64674bd600d8${opt}`, alt: "shop assistant serving a customer", pos: "50% 45%" } },
  { key: "offices", title: "Offices", subtitle: "Hours per client and project", image: { url: `${IMG}/photo-1497366216548-37526070297c${opt}`, alt: "small office team at work", pos: "50% 50%" } },
  { key: "hospitality", title: "Hospitality", subtitle: "Split shifts, settled fairly", image: { url: `${IMG}/photo-1517248135467-4c7edcad34c4${opt}`, alt: "cafe staff serving guests", pos: "50% 50%" } },
  { key: "wellness", title: "Gyms & studios", subtitle: "Cover across every location", image: { url: `${IMG}/photo-1534438327276-14e5300c3a48${opt}`, alt: "fitness studio floor", pos: "50% 45%" } },
  { key: "healthcare", title: "Care & clinics", subtitle: "Shifts, visits and certificates", image: { url: `${IMG}/photo-1576091160399-112ba8d25d1d${opt}`, alt: "care worker with a patient", pos: "50% 40%" } },
];

/** Gallery items with each photo resolved to its CMS slot (baked fallback). */
export const industryGallery = (t: TFn): GalleryItem[] =>
  GALLERY.map(({ key, image, ...rest }) => ({
    ...rest,
    image: { ...image, url: cmsImage(t, `industries:bento.photo.${key}`, image.url) },
  }));

export default function IndustriesGallery() {
  const { t } = useLocale();
  return (
    <section className="overflow-hidden bg-bg" aria-label="Industries we serve">
      <div className="h-[520px] w-full">
        <CircularGallery items={industryGallery(t)} />
      </div>
    </section>
  );
}
